/**
 * Question API Service with Fix
 * Enhanced version of questionService with better handling of real vs mock data
 */
import apiService from './apiService';
import questionFormatService from './questionFormatService';

class QuestionServiceFix {
  basePath = '/api/question';

  /**
   * Get the count of available questions based on filter criteria
   * @param {Object} filterData - Question filter parameters matching QuestionFilterRequestModel
   * @returns {Promise<number>} - Count of matching questions
   */
  async getQuestionsCount(filterData = {}) {
    return apiService.post(`${this.basePath}/count`, filterData);
  }
  
  /**
   * Fetch detailed question information including answers and explanations
   * @param {number} questionId - The ID of the question to fetch details for
   * @returns {Promise<Object>} - Detailed question object with answers and explanations
   */
  async getQuestionDetails(questionId) {
    try {
      if (!questionId) {
        console.error('Cannot fetch question details: questionId is undefined');
        return null;
      }
      
      console.log(`Fetching details for question ID: ${questionId}`);
      const questionDetails = await apiService.get(`${this.basePath}/${questionId}`);
      console.log('Question details fetched:', questionDetails);
      
      return this.formatQuestion(questionDetails);
    } catch (error) {
      console.error(`Error fetching question details for ID ${questionId}:`, error);
      return null;
    }
  }
  
  /**
   * Format a question from API response to consistent structure
   * @param {Object} questionData - Raw question data from API
   * @returns {Object} - Formatted question object
   */
  formatQuestion(questionData) {
    return questionFormatService.formatQuestionFromResponse(questionData);
  }
  /**
   * Get current question with proper formatting from questions array
   * @param {Array} questions - Array of questions
   * @param {number} currentIndex - Current question index
   * @param {Array} mockQuestions - Fallback mock questions if API data is not available
   * @returns {Object} - Formatted current question
   */
  getCurrentQuestion(questions, currentIndex, mockQuestions = []) {
    const callId = Math.random().toString(36).substring(2, 8);
    console.log(`[getCurrentQuestion][${callId}] Called with:`, {
      questionsLength: questions ? questions.length : 'questions is null/undefined',
      currentIndex,
      mockQuestionsLength: mockQuestions ? mockQuestions.length : 'mockQuestions is null/undefined',
      timestamp: new Date().toISOString()
    });
    
    // Enhanced check to handle various edge cases
    if (questions && Array.isArray(questions) && questions.length > 0 && 
        currentIndex >= 0 && currentIndex < questions.length) {
      const question = questions[currentIndex];
      console.log(`[getCurrentQuestion][${callId}] ✅ Using real API data for question:`, {
        index: currentIndex,
        questionId: question.id || question.questionId,
        questionText: question.questionText?.substring(0, 50) + '...'
      });
      return this.formatQuestion(question);
    }
    
    // If we reach here, log that we're falling back to mock data and why
    console.warn(`[getCurrentQuestion][${callId}] ⚠️ Falling back to mock data because:`, {
      reason: !questions ? 'questions is null/undefined' : 
              !Array.isArray(questions) ? 'questions is not an array' :
              questions.length === 0 ? 'questions array is empty' :
              currentIndex < 0 ? 'currentIndex is negative' :
              currentIndex >= questions.length ? `currentIndex (${currentIndex}) >= questions.length (${questions.length})` :
              'unknown reason',
      timestamp: new Date().toISOString()
    });
    
    // Fall back to mock data if API data is not available
    return mockQuestions[currentIndex % mockQuestions.length];
  }

  /**
   * Get formatted options for a question
   * @param {Object} question - Question object
   * @returns {Array} - Formatted options
   */
  getFormattedOptions(question) {
    return questionFormatService.getFormattedOptions(question);
  }

  /**
   * Extract clues from formatted question text
   * @param {string} formattedText - Formatted question text
   * @returns {Array} - Extracted clues
   */
  extractClues(formattedText) {
    return questionFormatService.extractClues(formattedText);
  }
  /**
   * Get question text with highlighted clues
   * @param {Object} question - Question object
   * @param {boolean} showClues - Whether to show clues
   * @returns {string} - Formatted question text
   */
  getQuestionWithClues(question, showClues) {
    // Enhanced debugging for question structure
    console.log('getQuestionWithClues called with question:', 
      question ? 
        `ID: ${question.id || question.questionId}, Type: ${question.isRealApiData ? 'API' : 'Mock'}, Properties: ${Object.keys(question).join(', ')}` : 
        'null/undefined'
    );
    
    if (!question) return "Question data is not available";
    
    // Check if it's API data and use appropriate fields
    if (question.isRealApiData) {
      // Use appropriate field for API questions
      let formattedText = "";
      if (question.bodyFormatted) {
        formattedText = question.bodyFormatted;
        console.log('Using bodyFormatted field');
      } else if (question.body) {
        formattedText = question.body;
        console.log('Using body field');
      } else if (question.text) {
        formattedText = question.text;
        console.log('Using text field');
      } else {
        // If no direct question text found, try to look deeper
        console.warn('No direct question text found, attempting to extract from structure');
        if (question.question) {
          if (typeof question.question === 'string') {
            formattedText = question.question;
            console.log('Using string question field');
          } else if (typeof question.question === 'object') {
            formattedText = question.question.body || question.question.bodyFormatted || 
                          question.question.text || JSON.stringify(question.question);
            console.log('Using question object field');
          }
        } else {
          // Last resort - try to use a stringified version of the question
          formattedText = `Failed to extract question text. Available fields: ${Object.keys(question).join(', ')}`;
          console.error('Failed to extract question text from API data');
        }
      }
      
      return questionFormatService.getQuestionWithClues({
        bodyFormatted: formattedText
      }, showClues);
    } 
    // Handle mock question format differently
    else {
      // For mock questions that use the 'question' property as a string
      if (question.question && typeof question.question === 'string') {
        console.log('Using mock question format with question string property');
        return questionFormatService.getQuestionWithClues({
          bodyFormatted: question.question
        }, showClues);
      }
      // Return the question as is if it's already a string
      else if (typeof question === 'string') {
        console.log('Question is already a string');
        return questionFormatService.getQuestionWithClues({
          bodyFormatted: question
        }, showClues);
      }
    }
    
    // Fallback to original implementation
    return questionFormatService.getQuestionWithClues(question, showClues);
  }

  /**
   * Check if selected answer is correct
   * @param {Object} question - Question object
   * @param {string} selectedAnswer - Selected answer letter
   * @returns {boolean} - Whether the selected answer is correct
   */
  isSelectedAnswerCorrect(question, selectedAnswer) {
    if (!question || !selectedAnswer) {
      return false;
    }
    
    return selectedAnswer === question.correctAnswer;
  }

  /**
   * Get the ID of the correct answer
   * @param {Object} question - Question object
   * @returns {string|null} - ID of the correct answer
   */
  getCorrectAnswerId(question) {
    if (!question || !question.correctAnswer || !question.answers) {
      return null;
    }
    
    const index = question.correctAnswer.charCodeAt(0) - 65;
    return question.answers[index]?.id || null;
  }

  /**
   * Get explanation text for a question
   * @param {Object} question - Question object
   * @returns {string} - Explanation text
   */
  getExplanationText(question) {
    return question?.explanation || '';
  }

  /**
   * Fetch a question by ID
   * @param {number} questionId - Question ID
   * @returns {Promise<Object>} - Question object
   */
  async fetchQuestionById(questionId) {
    return this.getQuestionDetails(questionId);
  }
}

export default new QuestionServiceFix();
